import { useState, useEffect } from 'react';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface ProfileChange {
  field: string;
  oldValue: any;
  newValue: any;
}

interface PendingUpdate {
  id: string;
  tutorId: string;  // Keep for compatibility
  tutor_id: string; // PocketBase field
  tutor_name: string;
  tutor_email: string;
  originalData: any; // Keep for compatibility
  updatedData: any;  // Keep for compatibility
  pending_data: any; // PocketBase field
  current_data: any; // PocketBase field
  updateType: 'profile_edit' | 'profile_picture';
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string; // Keep for compatibility
  created: string;     // PocketBase field
  updated: string;     // PocketBase field
  reviewedAt?: string;
  reviewedBy?: string;
  adminNotes?: string;
  changes: ProfileChange[];
  priority: number;
  expand?: {
    tutorId?: {
      id: string;
      name: string;
      user: string;
      expand?: {
        user?: {
          id: string;
          name: string;
          email: string;
        };
      };
    };
  };
}

export const useTutorProfileApproval = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [pendingUpdates, setPendingUpdates] = useState<PendingUpdate[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasActivePendingUpdate, setHasActivePendingUpdate] = useState<boolean | null>(false);
  const [isInitialLoad, setIsInitialLoad] = useState(false);

  // Set up real-time subscription for profile updates
  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'tutor')) {
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupSubscription = async () => {
      try {
        unsubscribe = await pb.collection('tutor_pending_updates').subscribe('*', (e) => {
          // Refetch pending updates when any change occurs
          if (e.action === 'create' || e.action === 'update' || e.action === 'delete') {
            checkPendingUpdates();
          }
        });
      } catch (error) {
        console.error('Error setting up profile updates subscription:', error);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.id, user?.role]);

  // Calculate changes between original and updated data
  const calculateChanges = (originalData: any, updatedData: any): ProfileChange[] => {
    const changes: ProfileChange[] = [];
    
    // Fields to exclude from change tracking (system fields, timestamps, etc.)
    const excludedFields = [
      'id', 'created', 'updated', 'collectionId', 'collectionName',
      'user', 'expand', 'verified', 'profileCompleted', 'isPending',
      'role', 'isApproved', 'isActive', 'isFrozen', 'lastSeen',
      'onlineStatus', 'autoApprovalEnabled', 'rating', 'reviewCount',
      'adminNotes', 'isOnline', 'reached', 'responseTimeHours', 'totalLessons',
      'heroCountries', 'moreGuides', 'isHeroGuide', 'isFeatured', 
      'instant_session_approved', 'instant_session_notes'
    ];
    
    // Get all unique keys from both objects
    const allKeys = new Set([
      ...Object.keys(originalData || {}),
      ...Object.keys(updatedData || {})
    ]);
    
    // Helper function to check if value is empty/not set
    const isEmpty = (value: any) => {
      if (value === null || value === undefined || value === '') {
        return true;
      }
      if (Array.isArray(value) && value.length === 0) {
        return true;
      }
      if (typeof value === 'object' && Object.keys(value).length === 0) {
        return true;
      }
      return false;
    };
    
    // Check each field for changes
    allKeys.forEach(field => {
      // Skip excluded fields
      if (excludedFields.includes(field)) {
        return;
      }
      
      const oldValue = originalData?.[field];
      const newValue = updatedData?.[field];
      
      const oldIsEmpty = isEmpty(oldValue);
      const newIsEmpty = isEmpty(newValue);
      
      // Only track changes if:
      // 1. Old value was set and new value is different
      // 2. Old value was not set and new value IS set (adding new data)
      if (!oldIsEmpty && !newIsEmpty) {
        // Both have values, check if they're different
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes.push({ field, oldValue, newValue });
        }
      } else if (oldIsEmpty && !newIsEmpty) {
        // Adding new data where there was none
        changes.push({ field, oldValue, newValue });
      } else if (!oldIsEmpty && newIsEmpty) {
        // Removing data (value to empty)
        changes.push({ field, oldValue, newValue });
      }
      // Skip if both are empty (oldIsEmpty && newIsEmpty)
    });

    return changes;
  };

  // Check if tutor has pending updates
  const checkPendingUpdates = async (tutorId?: string) => {
    if (!user || (user.role !== 'admin' && user.role !== 'tutor')) {
      setHasActivePendingUpdate(false);
      setIsInitialLoad(false);
      return;
    }

    try {
      let filter = '';
      let records: any;
      
      if (user.role === 'admin') {
        // Admin can see all pending updates
        filter = 'status = "pending"';
        records = await pb.collection('tutor_pending_updates').getList(1, 100, {
          filter,
          sort: '-created'
        });
      } else {
        // For tutors, first try matching by tutor_id
        const targetId = tutorId || user.id;
        filter = `status = "pending" && tutor_id = "${targetId}"`;
        records = await pb.collection('tutor_pending_updates').getList(1, 100, {
          filter,
          sort: '-created'
        });

        // If no results and we have user name/email, try matching by name/email
        if (records.items.length === 0 && user.name && user.email) {
          const nameEmailFilter = `status = "pending" && tutor_name = "${user.name}" && tutor_email = "${user.email}"`;
          
          records = await pb.collection('tutor_pending_updates').getList(1, 100, {
            filter: nameEmailFilter,
            sort: '-created'
          });
        }

        // If still no results, try expanding tutor_id relation and match user field
        if (records.items.length === 0) {
          const expandedRecords = await pb.collection('tutor_pending_updates').getList(1, 100, {
            filter: 'status = "pending"',
            expand: 'tutor_id',
            sort: '-created'
          });

          // Filter by user ID in the expanded tutor relation
          const matchingRecords = expandedRecords.items.filter((item: any) => 
            item.expand?.tutor_id?.user === user.id
          );

          if (matchingRecords.length > 0) {
            records = {
              ...expandedRecords,
              items: matchingRecords,
              totalItems: matchingRecords.length
            };
          }
        }
      }

      // Transform PocketBase data to match interface
      const transformedItems = records.items.map((item: any) => {
        const changes = calculateChanges(item.current_data || {}, item.pending_data || {});
        return {
          ...item,
          tutorId: item.tutor_id,
          tutor_id: item.tutor_id,
          tutor_name: item.tutor_name,
          tutor_email: item.tutor_email,
          originalData: item.current_data,
          updatedData: item.pending_data,
          pending_data: item.pending_data,
          current_data: item.current_data,
          submittedAt: item.created,
          reviewedAt: item.reviewed_at,
          reviewedBy: item.reviewed_by,
          adminNotes: item.admin_notes,
          changes: changes,
          priority: 2,
          updateType: 'profile_edit' as const
        };
      });
      
      setPendingUpdates(transformedItems as unknown as PendingUpdate[]);
      
      // Check if current tutor has pending updates
      if (user.role === 'tutor') {
        const hasPending = records.items.some((item: any) => 
          item.tutor_id === user.id || 
          (item.tutor_name === user.name && item.tutor_email === user.email) ||
          (item.expand?.tutor_id?.user === user.id)
        );
        setHasActivePendingUpdate(hasPending);
      } else {
        setHasActivePendingUpdate(false);
      }
      
      setIsInitialLoad(false);

    } catch (error) {
      console.error('Error checking pending updates:', error);
      setHasActivePendingUpdate(false);
      setIsInitialLoad(false);
    }
  };

  // Fetch profile update notifications
  const fetchNotifications = async () => {
    if (!user || user.role !== 'admin') {
      return;
    }

    try {
      const records = await pb.collection('profile_update_notifications').getList(1, 50, {
        sort: '-created',
        filter: 'is_read = false'
      });

      setNotifications(records.items);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Submit profile update request
  const submitProfileUpdate = async (
    tutorId: string, 
    originalData: any, 
    updatedData: any, 
    updateType: 'profile_edit' | 'profile_picture' = 'profile_edit'
  ) => {
    if (!user || user.role !== 'tutor') {
      throw new Error('Only tutors can submit profile updates');
    }

    setLoading(true);
    try {
      // Check if there's already a pending update (allow resubmission if previous was rejected)
      const existingPending = await pb.collection('tutor_pending_updates').getList(1, 1, {
        filter: `tutor_id = "${tutorId}" && status = "pending"`
      });

      if (existingPending.items.length > 0) {
        throw new Error('You already have a pending profile update. Please wait for admin approval.');
      }

      // Check for rejected updates and allow resubmission
      const rejectedUpdates = await pb.collection('tutor_pending_updates').getList(1, 1, {
        filter: `tutor_id = "${tutorId}" && status = "rejected"`,
        sort: '-created'
      });

      // If there was a rejected update, we can proceed with new submission
      // No need to block - tutors should be able to resubmit after rejection

      const changes = calculateChanges(originalData, updatedData);
      
      if (changes.length === 0) {
        throw new Error('No changes detected in the profile');
      }

      // Determine priority based on change types
      let priority = 2; // default medium
      const highPriorityFields = ['personalEmail', 'phoneNumber', 'profilePicture'];
      const hasHighPriorityChanges = changes.some(change => 
        highPriorityFields.includes(change.field)
      );
      if (hasHighPriorityChanges) priority = 1;

      // Use email from updated data or original data (no need for additional query)
      const tutorEmail = updatedData.personalEmail || originalData.personalEmail || user.email || 'unknown@email.com';

      // Create update request - handle file uploads for profile photo and video
      let updateRequest;
      
      const hasPhotoFile = updatedData.profilePicture && updatedData.profilePicture instanceof File;
      const hasVideoFile = updatedData.introVideoFile && updatedData.introVideoFile instanceof File;
      
      if (hasPhotoFile || hasVideoFile) {
        const formData = new FormData();
        
        // Add the photo file if present
        if (hasPhotoFile) {
          formData.append('profile_photo', updatedData.profilePicture);
        }
        
        // Add the video file if present
        if (hasVideoFile) {
          formData.append('intro_video_file', updatedData.introVideoFile);
        }
        
        // Create a clean copy of updatedData without File objects for JSON serialization
        const cleanUpdatedData = { ...updatedData };
        if (hasPhotoFile) {
          delete cleanUpdatedData.profilePicture;
          delete cleanUpdatedData.hasNewPhoto;
        }
        if (hasVideoFile) {
          delete cleanUpdatedData.introVideoFile;
          delete cleanUpdatedData.hasNewVideo;
        }
        
        // Add other fields
        formData.append('tutor_id', tutorId);
        formData.append('tutor_name', originalData.name || 'Unknown');
        formData.append('tutor_email', tutorEmail);
        formData.append('status', 'pending');
        formData.append('pending_data', JSON.stringify(cleanUpdatedData));
        formData.append('current_data', JSON.stringify(originalData));
        formData.append('current_photo_url', originalData.profilePicture || '');
        formData.append('current_video_url', originalData.introVideo || '');
        
        updateRequest = await pb.collection('tutor_pending_updates').create(formData);
      } else {
        // No file upload, just regular data
        updateRequest = await pb.collection('tutor_pending_updates').create({
          tutor_id: tutorId,
          tutor_name: originalData.name || 'Unknown',
          tutor_email: tutorEmail,
          pending_data: updatedData,
          current_data: originalData,
          current_photo_url: originalData.profilePicture || '',
          current_video_url: originalData.introVideo || '',
          status: 'pending'
        });
      }

      // Create notification for admin
      await pb.collection('profile_update_notifications').create({
        tutor_id: tutorId,
        update_request_id: updateRequest.id,
        type: 'pending',
        message: `Profile update request submitted for review (${changes.length} changes)`,
        is_read: false
      });

      // Also create admin notification in main notifications collection
      // Get all admin users
      try {
        const adminUsers = await pb.collection('users').getFullList({
          filter: 'role = "admin"'
        });

        // Create notification for each admin
        for (const admin of adminUsers) {
          await pb.collection('notifications').create({
            userId: admin.id,
            type: 'admin_notification',
            title: 'New Profile Update Request',
            message: `${originalData.name || 'A tutor'} has submitted a profile update for review (${changes.length} changes)`,
            isRead: false,
            createdAt: new Date().toISOString(),
            data: {
              originalType: 'profile_update',
              updateRequestId: updateRequest.id,
              tutorId: tutorId,
              tutorName: originalData.name,
              changesCount: changes.length
            }
          });
        }
      } catch (error) {
        console.error('Error creating admin notifications:', error);
      }

      setHasActivePendingUpdate(true);
      
      return updateRequest;

    } catch (error: any) {
      console.error('Error submitting profile update:', error);
      console.error('PocketBase error details:', {
        message: error.message,
        status: error.status,
        response: error.response,
        data: error.data,
        originalError: error.originalError
      });
      toast({
        title: "Error",
        description: error.message || "Failed to submit profile update",
        variant: "destructive"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Approve update request (admin only)
  const approveUpdate = async (requestId: string, adminNotes?: string) => {
    if (!user || user.role !== 'admin') {
      throw new Error('Only admins can approve updates');
    }

    setLoading(true);
    try {
      const request = await pb.collection('tutor_pending_updates').getOne(requestId, {
        expand: 'tutorId'
      });

      // Update the request status first
      await pb.collection('tutor_pending_updates').update(requestId, {
        status: 'approved',
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
        admin_notes: adminNotes || ''
      });

      // Prepare update data for tutor profile
      let updateData = { ...request.pending_data };
      
      // Normalize array fields - ensure they're always arrays, not strings
      const arrayFields = ['subjects', 'languages', 'specializations', 'grades', 'paymentMethods', 'heroCountries'];
      arrayFields.forEach(field => {
        if (updateData[field]) {
          if (typeof updateData[field] === 'string') {
            // Convert string to array
            try {
              const parsed = JSON.parse(updateData[field]);
              updateData[field] = Array.isArray(parsed) ? parsed : [updateData[field]];
            } catch {
              // If it's not JSON, split by comma
              updateData[field] = updateData[field].split(',').map((item: string) => item.trim()).filter((item: string) => item);
            }
          } else if (!Array.isArray(updateData[field])) {
            // Ensure it's an array
            updateData[field] = [updateData[field]];
          }
        }
      });

      // Handle photo and video file transfers if present
      const hasPhotoFile = request.profile_photo;
      const hasVideoFile = request.intro_video_file;
      
      if (hasPhotoFile || hasVideoFile) {
        try {
          // Create FormData for tutor update
          const formData = new FormData();
          
          // Handle photo transfer
          if (hasPhotoFile) {
            const photoUrl = pb.files.getURL(request, request.profile_photo);
            const photoResponse = await fetch(photoUrl);
            if (!photoResponse.ok) {
              throw new Error(`Failed to fetch photo: ${photoResponse.status}`);
            }
            const photoBlob = await photoResponse.blob();
            formData.append('profilePicture', photoBlob, request.profile_photo);
          }
          
          // Handle video transfer
          if (hasVideoFile) {
            const videoUrl = pb.files.getURL(request, request.intro_video_file);
            const videoResponse = await fetch(videoUrl);
            if (!videoResponse.ok) {
              throw new Error(`Failed to fetch video: ${videoResponse.status}`);
            }
            const videoBlob = await videoResponse.blob();
            
            // IMPORTANT: Use exact field name from tutors collection
            formData.append('introVideoFile', videoBlob, request.intro_video_file);
          }
          
          // Add all other update data (excluding files from pending_data)
          Object.keys(updateData).forEach(key => {
            if (key !== 'profilePicture' && key !== 'introVideoFile' && key !== 'hasNewPhoto' && key !== 'hasNewVideo') {
              const value = updateData[key];
              if (Array.isArray(value)) {
                // For arrays, send as JSON string (PocketBase will parse it correctly)
                formData.append(key, JSON.stringify(value));
              } else if (typeof value === 'object' && value !== null) {
                formData.append(key, JSON.stringify(value));
              } else if (value !== null && value !== undefined) {
                formData.append(key, value.toString());
              }
            }
          });
          
          // Mark profile as completed when approved
          formData.append('profileCompleted', 'true');
          
          // Update tutor profile with the file(s)
          const updatedTutor = await pb.collection('tutors').update(request.tutor_id, formData);
          
        } catch (error) {
          console.error('Error transferring file(s):', error);
          // If file transfer fails, update without the files
          delete updateData.profilePicture;
          delete updateData.introVideoFile;
          delete updateData.hasNewPhoto;
          delete updateData.hasNewVideo;
          updateData.profileCompleted = true;
          await pb.collection('tutors').update(request.tutor_id, updateData);
        }
      } else if (updateData.profilePicture && typeof updateData.profilePicture === 'string') {
        // Handle URL-based profile picture
        updateData.profileCompleted = true;
        await pb.collection('tutors').update(request.tutor_id, updateData);
      } else {
        // No file updates, just update other fields
        delete updateData.profilePicture;
        delete updateData.introVideoFile;
        delete updateData.hasNewPhoto;
        delete updateData.hasNewVideo;
        updateData.profileCompleted = true;
        await pb.collection('tutors').update(request.tutor_id, updateData);
      }

      // Notify both the tutor and admin
      await pb.collection('profile_update_notifications').create({
        tutor_id: request.tutor_id,
        update_request_id: requestId,
        type: 'approved',
        message: `Your profile update has been approved${adminNotes ? ': ' + adminNotes : ''}`,
        is_read: false
      });

      // Also create admin notification in main notifications collection
      await pb.collection('notifications').create({
        userId: user.id,
        type: 'admin_notification',
        title: 'Profile Update Approved',
        message: `Profile update for ${request.tutor_name} has been approved`,
        isRead: false,
        createdAt: new Date().toISOString(),
        data: {
          originalType: 'profile_update',
          updateRequestId: requestId,
          tutorId: request.tutor_id,
          tutorName: request.tutor_name
        }
      });

      toast({
        title: "Update Approved",
        description: "Profile update has been approved and applied.",
        variant: "default"
      });

      // Refresh pending updates
      await checkPendingUpdates();

    } catch (error: any) {
      console.error('Error approving update:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to approve update",
        variant: "destructive"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Reject update request (admin only)
  const rejectUpdate = async (requestId: string, adminNotes?: string) => {
    if (!user || user.role !== 'admin') {
      throw new Error('Only admins can reject updates');
    }

    setLoading(true);
    try {
      const request = await pb.collection('tutor_pending_updates').getOne(requestId, {
        expand: 'tutorId'
      });

      // Update the request status
      await pb.collection('tutor_pending_updates').update(requestId, {
        status: 'rejected',
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
        admin_notes: adminNotes || ''
      });

      // Notify both the tutor and admin
      await pb.collection('profile_update_notifications').create({
        tutor_id: request.tutor_id,
        update_request_id: requestId,
        type: 'rejected',
        message: `Your profile update has been rejected${adminNotes ? ': ' + adminNotes : ''}`,
        is_read: false
      });

      // Also create admin notification in main notifications collection
      await pb.collection('notifications').create({
        userId: user.id,
        type: 'admin_notification',
        title: 'Profile Update Rejected',
        message: `Profile update for ${request.tutor_name} has been rejected`,
        isRead: false,
        createdAt: new Date().toISOString(),
        data: {
          originalType: 'profile_update',
          updateRequestId: requestId,
          tutorId: request.tutor_id,
          tutorName: request.tutor_name
        }
      });

      toast({
        title: "Update Rejected",
        description: "Profile update has been rejected.",
        variant: "default"
      });

      // Refresh pending updates
      await checkPendingUpdates();

    } catch (error: any) {
      console.error('Error rejecting update:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reject update",
        variant: "destructive"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get pending updates count for admin badge
  const getPendingUpdatesCount = () => {
    return pendingUpdates.filter(update => update.status === 'pending').length;
  };

  // Add request deduplication flags
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
  const [isFetchingNotifications, setIsFetchingNotifications] = useState(false);

  // Enhanced checkPendingUpdates with deduplication and error handling
  const checkPendingUpdatesWithDeduplication = async (tutorId?: string) => {
    if (isCheckingUpdates) return; // Prevent duplicate requests
    
    setIsCheckingUpdates(true);
    try {
      await checkPendingUpdates(tutorId);
    } catch (error) {
      console.error('Error in checkPendingUpdates:', error);
    } finally {
      setIsCheckingUpdates(false);
    }
  };

  // Enhanced fetchNotifications with deduplication and error handling
  const fetchNotificationsWithDeduplication = async () => {
    if (isFetchingNotifications) return; // Prevent duplicate requests
    
    setIsFetchingNotifications(true);
    try {
      await fetchNotifications();
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
    } finally {
      setIsFetchingNotifications(false);
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    const initializeData = async () => {
      if (!user || !isMounted) {
        setIsInitialLoad(false);
        return;
      }

      try {
        await checkPendingUpdatesWithDeduplication();
        if (user.role === 'admin' && isMounted) {
          await fetchNotificationsWithDeduplication();
        }
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        if (isMounted) {
          setIsInitialLoad(false);
        }
      }
    };

    // Increased debounce time to prevent rapid calls
    timeoutId = setTimeout(initializeData, 1000);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [user?.id, user?.role]); // Only depend on specific user properties

  // Polling effect for notifications (admin only)
  useEffect(() => {
    if (!user?.role || user.role !== 'admin') return;

    const interval = setInterval(async () => {
      try {
        await fetchNotificationsWithDeduplication();
      } catch (error) {
        console.error('Error in notification polling:', error);
      }
    }, 60000); // Poll every 60 seconds to reduce load

    return () => clearInterval(interval);
  }, [user?.role]);

  // Polling effect for pending updates
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      try {
        await checkPendingUpdatesWithDeduplication();
      } catch (error) {
        console.error('Error in pending updates polling:', error);
      }
    }, 60000); // Poll every 60 seconds to reduce load

    return () => clearInterval(interval);
  }, [user?.id]);

  // Check if user has any rejected profiles (for allowing resubmission)
  const hasRejectedProfile = async (tutorId: string) => {
    try {
      const rejected = await pb.collection('tutor_pending_updates').getList(1, 1, {
        filter: `tutor_id = "${tutorId}" && status = "rejected"`,
        sort: '-created'
      });
      return rejected.items.length > 0;
    } catch (error) {
      console.error('Error checking rejected profiles:', error);
      return false;
    }
  };

  return {
    pendingUpdates,
    notifications,
    loading,
    hasActivePendingUpdate,
    isInitialLoad,
    submitProfileUpdate,
    approveUpdate,
    rejectUpdate,
    checkPendingUpdates: checkPendingUpdatesWithDeduplication,
    fetchNotifications: fetchNotificationsWithDeduplication,
    getPendingUpdatesCount,
    calculateChanges,
    hasRejectedProfile
  };
};
